import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert, Card, CardContent } from '@mui/material';
import { apiClient } from '../../lib/apiClient';

function ChurchAdminDashboard({ user }) {
  const [church, setChurch] = useState(null);
  const [stats, setStats] = useState({
    announcements: 0,
    sermons: 0,
    members: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get user's churches (should be at least one for admin)
        const myChurches = await apiClient.getMyChurches(user.id);
        
        if (myChurches.length === 0) {
          setError('You are not assigned to any church');
          return;
        }

        // Use first church (or could let user select)
        const currentChurch = myChurches[0];
        setChurch(currentChurch);
        // Store in localStorage for header to display
        localStorage.setItem('selectedChurch', JSON.stringify(currentChurch));
        window.dispatchEvent(new Event('churchSelected'));

        // Fetch stats
        const [announcements, sermons] = await Promise.all([
          apiClient.getAnnouncements(currentChurch.id),
          apiClient.getSermons(currentChurch.id)
        ]);

        // Get all users and count members of this church
        const allUsers = await apiClient.getAllUsers();
        const members = allUsers.filter(u => u.churchIds?.includes(currentChurch.id));

        setStats({
          announcements: announcements.length,
          sermons: sermons.length,
          members: members.length
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Church Admin Dashboard
      </Typography>
      {church && (
        <Card sx={{ mt: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{church.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {church.address?.city}, {church.address?.state}
            </Typography>
          </CardContent>
        </Card>
      )}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Announcements</Typography>
            <Typography variant="h4" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              {stats.announcements}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Sermons</Typography>
            <Typography variant="h4" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              {stats.sermons}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Members</Typography>
            <Typography variant="h4" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              {stats.members}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ChurchAdminDashboard;

